import { useQueries } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { useMe } from '@/hooks/useMe';
import { queryKeys } from '@/query/keys';

/** Detects a 403 from the wrapped fetch error so the editor can render the
 * tailored permission banner. */
export function isForbidden(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const status = (err as { status?: unknown }).status;
  return status === 403;
}

/** Builds two-letter initials for avatar fallbacks. */
export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.charAt(0) ?? '?').toUpperCase();
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts[parts.length - 1]?.charAt(0) ?? '';
  return `${first}${last}`.toUpperCase();
}

/**
 * Per-user resolution state exposed to renderers. `pending` while the join
 * against the directory is in flight — callers show a skeleton and MUST NOT
 * fall back to the raw userId so the UI never flashes a GUID.
 */
export type ResolvedName =
  | { readonly status: 'pending' }
  | { readonly status: 'ready'; readonly name: string | null };

/**
 * Panel-facing resolution result: the null-name case is already replaced with
 * a translated fallback label so renderers can render `resolved.name` directly.
 */
export type ResolvedMemberName =
  | { readonly status: 'pending' }
  | { readonly status: 'ready'; readonly name: string };

/**
 * Render-ready label for one resolved name: `null` while the lookup is pending (render
 * blank, never the guid), the name once resolved, or `unknownLabel` when the lookup
 * failed or the user has no name.
 */
export function resolvedNameLabel(
  resolved: ResolvedName | undefined,
  unknownLabel: string,
): string | null {
  if (resolved === undefined || resolved.status === 'pending') return null;
  return resolved.name ?? unknownLabel;
}

/**
 * Resolves a batch of tenant user ids to display names via `GET /staff/{guid}`
 * (PTV2-1567), which any signed-in user may call.
 *
 * Records (tasks, pathway steps, care-team members, pedigrees, …) carry bare user
 * guids, so renderers join against the staff directory. We fan out per-user queries
 * through `useQueries` so each lookup caches independently (a userId appearing
 * across multiple panels only fires the network once). Returns a per-id status
 * so the caller can render a skeleton for pending ids instead of leaking the
 * raw GUID until the query resolves.
 *
 * The signed-in user is resolved from `/me` with no request, and the fan-out is
 * held while `/me` is in flight so the common "it was me" case never waits on a
 * staff lookup.
 */
export function useResolveUserDisplayNames(
  userIds: readonly string[],
): ReadonlyMap<string, ResolvedName> {
  const me = useMe();
  const meId = me.data?.id;
  const unique = Array.from(new Set(userIds.filter((id) => id.length > 0)));
  const toFetch = unique.filter((id) => id !== meId);
  const queries = useQueries({
    queries: toFetch.map((id) => ({
      queryKey: queryKeys.staff(id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiClient.getStaffMember(id, { signal }),
      enabled: !me.isPending,
      staleTime: 5 * 60_000,
      gcTime: 30 * 60_000,
      retry: false,
    })),
  });

  const out = new Map<string, ResolvedName>();
  for (const id of unique) {
    if (me.isPending) {
      out.set(id, { status: 'pending' });
      continue;
    }
    if (me.data !== undefined && id === meId) {
      const selfName = me.data.fullName || me.data.email;
      out.set(id, { status: 'ready', name: selfName.length > 0 ? selfName : null });
      continue;
    }
    const result = queries[toFetch.indexOf(id)];
    if (result === undefined || result.isPending) {
      out.set(id, { status: 'pending' });
      continue;
    }
    if (!result.isSuccess) {
      out.set(id, { status: 'ready', name: null });
      continue;
    }
    const displayName = result.data.display_name?.trim() ?? '';
    out.set(id, { status: 'ready', name: displayName.length > 0 ? displayName : null });
  }
  return out;
}
